import React, { useState } from "react";
import { theme, Select, Button, Input, Space, Popconfirm, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useVehicleLookup } from "../qv4/viewmodels/shared/useVehicleLookup";
import { usePartInfoOptions } from "../qv4/viewmodels/shared/usePartInfoOptions";
import { addPartInfo, normalizePartInfoList } from "../qv4/models/shared/partInfoModel";

// Admin-only utility to grow the shared brand/model/part master lists — same
// backend endpoints (add_makes_v2/add_model_v2/add_new_part_info) and
// brand-first flow as the "New Query" modal's inline "+" pattern, just
// standalone here rather than attached to picking a vehicle for a specific
// query. CustomerEditDrawer.js's own
// vehicle sub-form (QuerysetV4) has no equivalent add-new capability today —
// this is purely additive (no edit/delete), matching what was asked for.
export function VehicleReferencePanel() {
  const { token } = theme.useToken();
  const {
    brands: brandOptions, brandsLoading, addBrand,
    models: modelOptions, modelsLoading, loadModels, addModel,
  } = useVehicleLookup();
  // usePartInfoOptions' own addOption appends optimistically without using
  // the response body — this modal needs the response body (to find and
  // auto-select the newly added part by its returned id), so it manages
  // partOptions via the hook's setOptions but calls the model directly for
  // the add itself, same pattern used for brand/model below.
  const { options: partOptions, loading: partsLoading, setOptions: setPartOptions } = usePartInfoOptions();

  const [brandId, setBrandId] = useState(null);
  const [addingBrand, setAddingBrand] = useState(false);
  const [newBrandName, setNewBrandName] = useState("");
  const [savingBrand, setSavingBrand] = useState(false);

  const [modelId, setModelId] = useState(null);
  const [addingModel, setAddingModel] = useState(false);
  const [newModelName, setNewModelName] = useState("");
  const [savingModel, setSavingModel] = useState(false);

  const [partId, setPartId] = useState(null);
  const [addingPart, setAddingPart] = useState(false);
  const [newPartName, setNewPartName] = useState("");
  const [savingPart, setSavingPart] = useState(false);

  const handleSelectBrand = (val) => {
    setBrandId(val);
    setModelId(null);
    loadModels(val);
  };

  // Case/whitespace-insensitive duplicate check against an already-loaded
  // options list — catches "Toyota" vs "toyota "/" TOYOTA" before wasting a
  // round-trip on a save the backend would likely just duplicate anyway
  // (these lists have no unique-name constraint enforced client-side).
  const findExisting = (list, name) =>
    list.find(o => String(o.label).trim().toLowerCase() === name.trim().toLowerCase());

  // Confirmation is only worth asking for the genuinely-risky path (a new,
  // permanent insert with no undo/delete in this UI). Selecting an existing
  // entry is a no-write action (handleAdd* just selects it), so it stays a
  // single click — same as before.
  const trimmedBrandName = newBrandName.trim();
  const existingBrandMatch = trimmedBrandName ? findExisting(brandOptions, trimmedBrandName) : null;
  const trimmedModelName = newModelName.trim();
  const existingModelMatch = trimmedModelName ? findExisting(modelOptions, trimmedModelName) : null;
  const trimmedPartName = newPartName.trim();
  const existingPartMatch = trimmedPartName ? findExisting(partOptions, trimmedPartName) : null;

  const handleAddBrand = async () => {
    const name = newBrandName.trim();
    if (!name) return;
    const existing = findExisting(brandOptions, name);
    if (existing) {
      message.warning(`"${existing.label}" already exists — selected it instead.`);
      handleSelectBrand(existing.value);
      setAddingBrand(false);
      setNewBrandName("");
      return;
    }
    setSavingBrand(true);
    try {
      const res = await addBrand(name);
      if (res.success) {
        const list = res.data.map(item => ({ value: item.id, label: item.name }));
        const added = list.find(o => o.label === name);
        if (added) handleSelectBrand(added.value);
        message.success("Brand added");
        setAddingBrand(false);
        setNewBrandName("");
      } else {
        message.error(res.message || "Failed to add brand");
      }
    } catch (err) {
      message.error(err.message || "Failed to add brand");
    } finally {
      setSavingBrand(false);
    }
  };

  const handleAddModel = async () => {
    const modelName = newModelName.trim();
    if (!modelName || !brandId) return;
    const existing = findExisting(modelOptions, modelName);
    if (existing) {
      message.warning(`"${existing.label}" already exists for this brand — selected it instead.`);
      setModelId(existing.value);
      setAddingModel(false);
      setNewModelName("");
      return;
    }
    setSavingModel(true);
    try {
      const res = await addModel(brandId, modelName);
      if (res.success) {
        const list = res.data.map(item => ({ value: item.id, label: item.model }));
        const added = list.find(o => o.label === modelName);
        if (added) setModelId(added.value);
        message.success("Model added");
        setAddingModel(false);
        setNewModelName("");
      } else {
        message.error(res.message || "Failed to add model");
      }
    } catch (err) {
      message.error(err.message || "Failed to add model");
    } finally {
      setSavingModel(false);
    }
  };

  const handleAddPart = async () => {
    const partName = newPartName.trim();
    if (!partName) return;
    const existing = findExisting(partOptions, partName);
    if (existing) {
      message.warning(`"${existing.label}" already exists — selected it instead.`);
      setPartId(existing.value);
      setAddingPart(false);
      setNewPartName("");
      return;
    }
    setSavingPart(true);
    try {
      const res = await addPartInfo(partName);
      if (res.success) {
        const list = normalizePartInfoList(res.data);
        setPartOptions(list);
        const added = list.find(o => o.label === partName);
        if (added) setPartId(added.value);
        message.success("Part added");
        setAddingPart(false);
        setNewPartName("");
      } else {
        message.error(res.message || "Failed to add part");
      }
    } catch (err) {
      message.error(err.message || "Failed to add part");
    } finally {
      setSavingPart(false);
    }
  };

  return (
      <div className="vehicle-reference-panel" style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 4 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: token.colorTextSecondary }}>Brand</div>
          {addingBrand ? (
            <Space.Compact style={{ width: "100%" }}>
              <Input
                autoFocus
                placeholder="New brand name"
                value={newBrandName}
                onChange={e => setNewBrandName(e.target.value)}
                onPressEnter={handleAddBrand}
              />
              {existingBrandMatch || !trimmedBrandName ? (
                <Button type="primary" loading={savingBrand} disabled={!trimmedBrandName} onClick={handleAddBrand}>Save</Button>
              ) : (
                <Popconfirm
                  title={`Add "${trimmedBrandName}" as a new brand?`}
                  description="This can't be undone from this screen."
                  onConfirm={handleAddBrand}
                  okText="Add" cancelText="Cancel"
                >
                  <Button type="primary" loading={savingBrand}>Save</Button>
                </Popconfirm>
              )}
              <Button onClick={() => { setAddingBrand(false); setNewBrandName(""); }}>Cancel</Button>
            </Space.Compact>
          ) : (
            <Space.Compact style={{ width: "100%" }}>
              <Select
                showSearch
                style={{ flex: 1 }}
                placeholder="Select a brand"
                loading={brandsLoading}
                options={brandOptions}
                value={brandId}
                onChange={handleSelectBrand}
                optionFilterProp="label"
              />
              <Button icon={<PlusOutlined />} onClick={() => setAddingBrand(true)} />
            </Space.Compact>
          )}
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: token.colorTextSecondary }}>Model</div>
          {addingModel ? (
            <Space.Compact style={{ width: "100%" }}>
              <Input
                autoFocus
                placeholder="New model name"
                value={newModelName}
                onChange={e => setNewModelName(e.target.value)}
                onPressEnter={handleAddModel}
              />
              {existingModelMatch || !trimmedModelName ? (
                <Button type="primary" loading={savingModel} disabled={!trimmedModelName} onClick={handleAddModel}>Save</Button>
              ) : (
                <Popconfirm
                  title={`Add "${trimmedModelName}" as a new model?`}
                  description="This can't be undone from this screen."
                  onConfirm={handleAddModel}
                  okText="Add" cancelText="Cancel"
                >
                  <Button type="primary" loading={savingModel}>Save</Button>
                </Popconfirm>
              )}
              <Button onClick={() => { setAddingModel(false); setNewModelName(""); }}>Cancel</Button>
            </Space.Compact>
          ) : (
            <Space.Compact style={{ width: "100%" }}>
              <Select
                showSearch
                style={{ flex: 1 }}
                placeholder={brandId ? "Select a model" : "Select a brand first"}
                disabled={!brandId}
                loading={modelsLoading}
                options={modelOptions}
                value={modelId}
                onChange={setModelId}
                optionFilterProp="label"
              />
              <Button icon={<PlusOutlined />} disabled={!brandId} onClick={() => setAddingModel(true)} />
            </Space.Compact>
          )}
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: token.colorTextSecondary }}>Part</div>
          {addingPart ? (
            <Space.Compact style={{ width: "100%" }}>
              <Input
                autoFocus
                placeholder="New part name"
                value={newPartName}
                onChange={e => setNewPartName(e.target.value)}
                onPressEnter={handleAddPart}
              />
              {existingPartMatch || !trimmedPartName ? (
                <Button type="primary" loading={savingPart} disabled={!trimmedPartName} onClick={handleAddPart}>Save</Button>
              ) : (
                <Popconfirm
                  title={`Add "${trimmedPartName}" as a new part?`}
                  description="This can't be undone from this screen."
                  onConfirm={handleAddPart}
                  okText="Add" cancelText="Cancel"
                >
                  <Button type="primary" loading={savingPart}>Save</Button>
                </Popconfirm>
              )}
              <Button onClick={() => { setAddingPart(false); setNewPartName(""); }}>Cancel</Button>
            </Space.Compact>
          ) : (
            <Space.Compact style={{ width: "100%" }}>
              <Select
                showSearch
                style={{ flex: 1 }}
                placeholder="Select a part"
                loading={partsLoading}
                options={partOptions}
                value={partId}
                onChange={setPartId}
                optionFilterProp="label"
              />
              <Button icon={<PlusOutlined />} onClick={() => setAddingPart(true)} />
            </Space.Compact>
          )}
        </div>
      </div>
  );
}
